import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { TbxTooltip } from '@/common/components'

const classes = {
    formControl_medium: {
        width: '180px',
    },
}

/**
 * Grain direction selector for flat quote items.
 * Allows users to select the grain direction for material cutting.
 * Memoized to prevent unnecessary re-renders when parent state changes.
 *
 * @param {Object} props - Component props
 * @param {string} props.value - The currently selected grain direction value
 * @param {Array<{value: string, label: string}>} props.options - Available grain direction options
 * @param {boolean} [props.disabled=false] - Whether the selector is disabled
 * @param {boolean} [props.isPartFromLibrary=false] - Whether the part is from the part library
 * @param {Function} props.onChange - Callback when selection changes
 * @returns {React.ReactElement} A row with label and grain direction dropdown
 */
const GrainDirectionSelector = ({ disabled = false, isPartFromLibrary = false, onChange, options, value }) => {
    const { t } = useTranslation()

    const tooltipTitle = isPartFromLibrary ? t('Cannot edit parts from library') : ''

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <Typography data-testid="nqp-quote-item-grain-direction-label">{t('Grain direction')}</Typography>
            <TbxTooltip
                title={tooltipTitle}
                arrow
            >
                <FormControl
                    color="primary"
                    margin="none"
                    size="small"
                    sx={classes.formControl_medium}
                    variant="outlined"
                >
                    <Select
                        data-testid="nqp-quote-item-grain-direction"
                        disabled={disabled}
                        id="select-itemGrainDirection"
                        labelId="select-itemGrainDirection-label"
                        SelectDisplayProps={{ style: { textAlign: 'right' } }}
                        value={value}
                        onChange={(e) => onChange(e)}
                    >
                        {options.map((direction) => (
                            <MenuItem
                                key={`direction-${direction.value}`}
                                value={direction.value}
                            >
                                {direction.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </TbxTooltip>
        </Box>
    )
}

GrainDirectionSelector.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        })
    ).isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    isPartFromLibrary: PropTypes.bool,
}

export default memo(GrainDirectionSelector)
